@if (isStandaloneLayout()) {
  <div class="relative">
    @if (offlineCount() > 0) {
      <div
        class="absolute right-0 top-0 z-50 block aspect-square w-2.5 rounded-full bg-text-common-signal"
      >
        <div
          class="absolute inset-0 animate-ping rounded-full bg-text-common-signal"
        ></div>
      </div>
    }
    <p-button
      size="small"
      pTooltip="View packhouse connectivity"
      tooltipPosition="left"
      [loading]="isLoading()"
      (click)="onStandaloneClick($event)"
    >
      <ng-template #icon>
        <svg-icon src="assets/icons/general/packhouse.svg" />
      </ng-template>
    </p-button>
  </div>
}

@if (isMenuLayout()) {
  <p-button
    variant="text"
    severity="contrast"
    styleClass="w-full !justify-start"
    [loading]="isLoading()"
    (click)="onMenuClick($event)"
  >
    <svg-icon src="assets/icons/general/packhouse.svg" />
    <span>Packhouse Connectivity</span>
    @if (!isLoading()) {
      <div
        class="typography-labels-medium-emphasis ml-auto grid aspect-square h-6 place-items-center rounded-full bg-interface-warning-muted-default px-1 text-text-warning-default"
      >
        {{ offlineCount() }}
      </div>
    }
  </p-button>
}

<ng-template #refreshStateTemplate>
  <ag1-time-since [timestamp]="lastRefreshedAt()" />
</ng-template>

<ng-template #packhouseConnectionTemplate let-packhouseConnection>
  <li class="rounded-lg border border-border-common-base">
    <div class="grid grid-cols-[1fr_auto] items-center gap-3 p-2">
      <div class="flex flex-col gap-1">
        <h3
          class="typography-headings-x-small truncate text-text-common-contrast"
        >
          {{ packhouseConnection?.foodBusinessOperator?.name }}
        </h3>
        <p class="typography-body-small text-text-common-base">
          Last sync
          {{
            $safeNavigationMigration(packhouseConnection?.lastProcessedDatetime)
              | duration
          }}
          ago
        </p>
      </div>

      <ag1-badge-small
        [variant]="packhouseConnection?.connected ? 'positive' : 'warning'"
        [label]="packhouseConnection?.connected ? 'Connected' : 'Offline'"
      />
    </div>

    <!-- footer -->
    <div
      class="flex items-center gap-3 border-t border-border-common-base bg-interface-surface-50 p-2"
    >
      <div class="flex items-center gap-1">
        <svg-icon
          class="text-text-common-base"
          src="assets/icons/general/system-feedback-code.svg"
        />
        <p class="typography-body-small text-text-common-base">
          {{ packhouseConnection?.onPremSystem }}
        </p>
      </div>

      <div class="flex items-center gap-1">
        <svg-icon
          class="text-text-common-base"
          src="assets/icons/general/data-database.svg"
        />
        <p class="typography-body-small text-text-common-base">
          {{ packhouseConnection?.name }}
        </p>
      </div>
    </div>
  </li>
</ng-template>

<ng-template #emptyTemplate>
  <div class="flex flex-col items-center gap-3 p-5">
    <svg-icon svgClass="h-10 w-10" src="assets/icons/general/packhouse.svg" />
    <div class="text-center">
      <h3 class="text-lg">You have no packhouse connections</h3>
      <span class="text-sm"> Add a packhouse connection to get started. </span>

      <!-- TODO: Add packhouse connection button once backend is implemented -->
      <!-- <p-button
        size="small"
        severity="primary"
        variant="text"
        (click)="addPackhouseConnection()"
      >
        Add Packhouse Connection
      </p-button> -->
    </div>
  </div>
</ng-template>

<ng-template #packhouseConnectionsTemplate let-packhouseConnections>
  <ul class="flex flex-col gap-2 pr-1" #packhouseConnectionsContainer>
    @for (
      packhouseConnection of packhouseConnections;
      track packhouseConnection.id
    ) {
      <ng-container
        *ngTemplateOutlet="
          packhouseConnectionTemplate;
          context: { $implicit: packhouseConnection }
        "
      />
    } @empty {
      <ng-container *ngTemplateOutlet="emptyTemplate" />
    }
  </ul>
</ng-template>

<ng-template #loadingTemplate>
  <div class="flex flex-col items-center gap-3 p-5">
    <ag1-loader />
    <div class="text-center">
      <h3 class="text-lg">Loading packhouse connections...</h3>
    </div>
  </div>
</ng-template>

<p-popover
  #packhouseConnectionsPopover
  (onShow)="popoverVisible.set(true)"
  (onHide)="popoverVisible.set(false)"
>
  <div class="!md:w-[30rem] flex flex-col gap-2">
    @if (isLoading()) {
      <ng-container *ngTemplateOutlet="loadingTemplate" />
    } @else {
      <div class="flex items-center gap-2">
        <h1 class="typography-headings-small p-1">Packhouse connectivity</h1>

        <p-button
          class="ml-auto"
          size="small"
          severity="contrast"
          variant="text"
          pTooltip="Refresh"
          tooltipPosition="left"
          [disabled]="isLoading() || isRefreshing()"
          (click)="refreshPackhouseConnections()"
        >
          <ng-template #icon>
            <svg-icon src="assets/icons/general/refresh.svg" />
          </ng-template>
        </p-button>

        <p-button
          *agHasPermission="permissionEnum.MasterfilesPackhouses"
          size="small"
          severity="contrast"
          variant="text"
          pTooltip="Settings"
          tooltipPosition="left"
          (click)="openPackhouseManager()"
        >
          <ng-template #icon>
            <svg-icon src="assets/icons/general/settings.svg" />
          </ng-template>
        </p-button>
      </div>

      <!-- on-prem installations -->
      <div class="max-h-[300px] overflow-y-auto">
        <ng-container
          [ngTemplateOutlet]="packhouseConnectionsTemplate"
          [ngTemplateOutletContext]="{ $implicit: packhouseConnections() }"
        />
      </div>

      <!-- refresh state -->
      <ng-container *ngTemplateOutlet="refreshStateTemplate" />
    }
  </div>
</p-popover>

<p-drawer
  #packhouseConnectionsDrawer
  styleClass="overflow-hidden !w-[30rem]"
  [visible]="drawerVisible()"
  (visibleChange)="drawerVisible.set($event)"
  position="right"
>
  <ng-template #header>
    <h1 class="typography-headings-small whitespace-nowrap p-1">
      Packhouse connectivity
    </h1>

    <p-button
      class="ml-auto"
      size="small"
      severity="contrast"
      variant="text"
      pTooltip="Refresh"
      tooltipPosition="left"
      [loading]="isLoading() || isRefreshing()"
      (click)="refreshPackhouseConnections()"
    >
      <ng-template #icon>
        <svg-icon src="assets/icons/general/refresh.svg" />
      </ng-template>
    </p-button>

    <p-button
      *agHasPermission="permissionEnum.MasterfilesPackhouses"
      size="small"
      severity="contrast"
      variant="text"
      pTooltip="Settings"
      tooltipPosition="left"
      (click)="openPackhouseManager()"
    >
      <ng-template #icon>
        <svg-icon src="assets/icons/general/settings.svg" />
      </ng-template>
    </p-button>
  </ng-template>

  @if (isLoading()) {
    <ng-container *ngTemplateOutlet="loadingTemplate" />
  } @else {
    <ag1-list-search
      class="mb-3 block"
      placeholder="Search packhouses"
      [keys]="['name', 'foodBusinessOperator.name']"
      [items]="packhouseConnections()"
      (filteredItemEvent)="filteredPackhouseConnections.set($event)"
    />

    <div class="h-[calc(100vh-14rem)] overflow-y-auto">
      <ng-container
        [ngTemplateOutlet]="packhouseConnectionsTemplate"
        [ngTemplateOutletContext]="{
          $implicit: filteredPackhouseConnections(),
        }"
      />
    </div>
  }

  <ng-template #footer>
    <ng-container *ngTemplateOutlet="refreshStateTemplate" />
  </ng-template>
</p-drawer>
